import * as Highcharts from 'highcharts';
import * as numeral from 'numeral';
import * as _ from 'underscore';

import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { UtilityService } from '../../../../core/utility/services/utility.service';
import { ColorPalette } from '../../../analytics-core/interfaces/partner.interface';
import { DownloadConfig } from '../../../analytics-core/interfaces/widget.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { EVENT_EMITTERS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { WidgetCore } from '../../interfaces/widget.core.interface';

@Component({
	selector: 'app-device-table',
	styleUrls: ['./device-table.component.css'],
	templateUrl: './device-table.component.html'
})
export class DeviceTableComponent implements OnInit, AfterViewInit, WidgetCore, OnDestroy {
	@ViewChild('chartElement', { static: true }) chartElement: ElementRef;

	public sectiontitle: string;
	public key: string;
	public colors: string;
	public groupby: string;
	@Input() downloadcsvcolumns: any;
	@Input() downloadconfig: string;
	@Input() sectionclass: string;

	public seriesData: any[] = [];
	public downloadCSVColumns: any;
	public householdData: any[] = [];
	public websiteAttributionsData: any[] = [];
	public tableData: any[] = [];
	public reorderable = false;
	public downloadConfiguration: DownloadConfig;

	public showLoad = true;
	public hasData = true;
	public donutColors: string[] = [];
	public colorPalette: ColorPalette;
	public chartObj: any;

	public totalObj: any = {};
	public totalCost = 0;
	public totalYoutubeVerifiedImprsRate = 0;
	public columns = [];
	public reloadSubscription;
	public widgetDataSubscription;
	private xAxisChartTitles: string[] = [];
	public youtubeImpressions: number;
	public youtubeViewsPermission: boolean = false;
	public youtubeViewsPermissionCheck: object = { permission: { flags: ['hasYoutubeViews'] } };

	constructor(
		protected analyticsService: AnalyticsService,
		protected colorUtilityService: ColorUtilityService,
		protected analyticsDataModel: AnalyticsDataModel
	) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.requestData();
		});
	}

	ngOnInit(): void {
		this.youtubeViewsPermission = this.analyticsDataModel.checkComponentForPermission(this.youtubeViewsPermissionCheck);
		this.downloadConfiguration = JSON.parse(decodeURI(this.downloadconfig));
		this.downloadCSVColumns = this.downloadcsvcolumns ? JSON.parse(decodeURI(this.downloadcsvcolumns)) : [];
	}

	ngAfterViewInit(): void {
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
		this.donutColors = this.colors.split(',');
		this.requestData();
	}

	ngOnDestroy(): void {
		if (this.chartObj) {
			this.chartObj.destroy();
		}
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	requestData(): void {
		this.hasData = true;
		this.showLoad = true;
		this.widgetDataSubscription = this.analyticsService.processWidgetRequest(this.key).subscribe((response: any) => {
			this.showLoad = false;
			if (response.success) {
				this.hasData = true;
				this.householdData = [];
				this.websiteAttributionsData = [];
				this.processResponseData(response.data);
			} else {
				this.hasData = false;
				this.seriesData = [];
				this.householdData = [];
				this.websiteAttributionsData = [];
				this.tableData = [];
			}
		});
	}

	processResponseData(responseData: any): void {
		this.totalCost = 0;
		const chartData: any[] = [];
		const tableData: any[] = [];
		const xAxisTitles: string[] = [];
		this.hasData = responseData.length > 0;
		this.youtubeImpressions = 0;
		for (let i = 0; i < responseData.length; i++) {
			const chartObj: any = {};
			chartObj.name = responseData[i].device;
			chartObj.color = this.colorUtilityService.getHEXByColorType(this.colorPalette, this.donutColors[i], 1);
			chartObj.y = responseData[i][this.groupby] * 1;
			chartData.push(chartObj);

			let clickRate;
			let cost;
			let clicks;
			let visits;
			let youtube_verified_imprs;
			let youtube_verified_imprs_rate;

			this.columns = [
				{
					cellClass: 'ctv-device',
					headerClass: 'ctv-deviceHeader',
					name: 'Device',
					prop: 'device',
					resizeable: false
				},
				{
					cellClass: 'ctv-impressions',
					comparator: this.impressionsComparator.bind(this),
					headerClass: 'ctv-impressionsHeader',
					name: 'Impressions',
					prop: 'formatted_impressions',
					resizeable: false
				}
			];

			if (responseData[i].clicks) {
				clicks = responseData[i].clicks;
				this.columns.push({
					cellClass: 'ctv-clicks',
					comparator: this.clicksComparator.bind(this),
					headerClass: 'ctv-clicksHeader',
					name: 'Clicks',
					prop: 'formatted_clicks',
					resizeable: false
				});
			}

			if (responseData[i].household_visits) {
				const chartObj: any = {};
				chartObj.name = responseData[i].device;
				chartObj.y = responseData[i].household_visits * 1;
				this.householdData.push(chartObj);
				visits = responseData[i].household_visits;
				this.columns.push({
					cellClass: 'ctv-impressions',
					comparator: this.visitsComparator.bind(this),
					headerClass: 'ctv-impressionsHeader',
					name: 'Household Site Visits',
					prop: 'formatted_household_visits',
					resizeable: false
				});
			}

			if (responseData[i].website_attributions) {
				const chartObj: any = {};
				chartObj.name = responseData[i].device;
				chartObj.y = responseData[i].website_attributions * 1;
				this.websiteAttributionsData.push(chartObj);
				visits = responseData[i].website_attributions;
				this.columns.push({
					cellClass: 'ctv-impressions',
					comparator: this.websiteAttributionsComparator.bind(this),
					headerClass: 'ctv-impressionsHeader',
					name: 'Website Attributions',
					prop: 'formatted_website_attributions',
					resizeable: false
				});
			}

			if (responseData[i].clickthroughrate) {
				clickRate = responseData[i].clickthroughrate;
				this.columns.push({
					cellClass: 'ctv-clickRate',
					comparator: this.clickRateComparator.bind(this),
					headerClass: 'ctv-clickRateHeader',
					name: 'Click Rate',
					prop: 'formatted_click_rate',
					resizeable: false
				});
			}

			if (clickRate && clickRate != null) {
				clickRate = parseFloat(clickRate).toFixed(2);
				clickRate = clickRate * 1;
			} else {
				clickRate = 0;
			}

			xAxisTitles.push(responseData[i].device);

			if (responseData[i].cost) {
				cost = responseData[i].cost;
				this.columns.push({
					cellClass: 'ctv-clickRate',
					comparator: this.costComparator.bind(this),
					headerClass: 'ctv-costHeader',
					name: 'Cost',
					prop: 'formatted_cost',
					resizeable: false
				});
				if (cost != null) {
					this.totalCost = this.totalCost + parseFloat(cost);
					cost = parseFloat(cost).toFixed(2);
					cost = cost * 1;
				} else {
					cost = 0;
				}
			}

			if (this.youtubeViewsPermission) {
				if (responseData[i].youtube_verified_imprs) {
					youtube_verified_imprs = responseData[i].youtube_verified_imprs;
					this.columns.push({
						cellClass: 'ctv-clickRate',
						comparator: this.youtubeImpressionsComparator.bind(this),
						headerClass: 'ctv-clicksHeader',
						name: 'YouTube Views',
						prop: 'formatted_youtube_verified_imprs',
						resizeable: false
					});
				}

				if (responseData[i].youtube_verified_imprs_rate) {
					youtube_verified_imprs_rate = responseData[i].youtube_verified_imprs_rate;
					this.columns.push({
						cellClass: 'ctv-clickRate',
						comparator: this.youtubeRateComparator.bind(this),
						headerClass: 'ctv-clicksHeader',
						name: 'YouTube View Rate',
						prop: 'formatted_youtube_verified_imprs_rate',
						resizeable: false
					});
					if (youtube_verified_imprs_rate != null) {
						this.totalYoutubeVerifiedImprsRate =
							this.totalYoutubeVerifiedImprsRate + parseFloat(youtube_verified_imprs_rate);
						youtube_verified_imprs_rate = parseFloat(youtube_verified_imprs_rate).toFixed(2);
						youtube_verified_imprs_rate = youtube_verified_imprs_rate * 1;
					} else {
						youtube_verified_imprs_rate = 0;
					}
				}

				if (responseData[i].youtube_imprs) {
					this.youtubeImpressions += parseInt(responseData[i].youtube_imprs);
				}
			}

			const obj = {
				device: responseData[i].device,
				formatted_impressions: numeral(responseData[i].impressions).format('0,0'),
				impressions: parseInt(responseData[i].impressions, 10)
			};

			if (responseData[i].clicks) {
				obj['clicks'] = parseInt(clicks, 10);
				obj['formatted_clicks'] = numeral(clicks).format('0,0');
			}

			if (responseData[i].household_visits) {
				obj['household_visits'] = parseInt(visits, 10);
				obj['formatted_household_visits'] = numeral(visits).format('0,0');
			}

			if (responseData[i].website_attributions) {
				obj['website_attributions'] = parseInt(visits, 10);
				obj['formatted_website_attributions'] = numeral(visits).format('0,0');
			}

			if (responseData[i].clickthroughrate) {
				obj['click_rate'] = clickRate;
				obj['formatted_click_rate'] = clickRate + '%';
			}

			if (responseData[i].cost) {
				obj['cost'] = cost;
				obj['formatted_cost'] = cost + '%';
			}

			if (responseData[i].youtube_verified_imprs) {
				obj['youtube_verified_imprs'] = parseInt(youtube_verified_imprs, 10);
				obj['formatted_youtube_verified_imprs'] = numeral(youtube_verified_imprs).format('0,0');
			}

			if (responseData[i].youtube_verified_imprs_rate) {
				obj['youtube_verified_imprs_rate'] = youtube_verified_imprs_rate;
				obj['formatted_youtube_verified_imprs_rate'] = youtube_verified_imprs_rate + '%';
			}

			tableData.push(obj);
		}
		this.xAxisChartTitles = xAxisTitles;
		this.seriesData = chartData;
		this.tableData = tableData;
		this.addFooterToTableData();
		this.drawDonutChart();
	}

	/**
	 *  Sorts Descending
	 * @param a
	 * @param b
	 * @param c
	 * @param d
	 * @returns {number}
	 */
	impressionsComparator(a: any, b: any, c: any, d: any): number {
		if (c.impressions < d.impressions) {
			return 1;
		}
		if (c.impressions > d.impressions) {
			return -1;
		}
	}

	visitsComparator(a: any, b: any, c: any, d: any): number {
		if (c.household_visits < d.household_visits) {
			return 1;
		}
		if (c.household_visits > d.household_visits) {
			return -1;
		}
	}

	clicksComparator(a: any, b: any, c: any, d: any): number {
		if (c.clicks < d.clicks) {
			return 1;
		}
		if (c.clicks > d.clicks) {
			return -1;
		}
	}

	householdVisitsComparator(a: any, b: any, c: any, d: any): number {
		if (c.household_visits < d.household_visits) {
			return 1;
		}
		if (c.household_visits > d.household_visits) {
			return -1;
		}
	}

	websiteAttributionsComparator(a: any, b: any, c: any, d: any): number {
		if (c.website_attributions < d.website_attributions) {
			return 1;
		}
		if (c.website_attributions > d.website_attributions) {
			return -1;
		}
	}

	costComparator(a: any, b: any, c: any, d: any): number {
		if (parseFloat(c.cost) < parseFloat(d.cost)) {
			return 1;
		}
		if (parseFloat(c.cost) > parseFloat(d.cost)) {
			return -1;
		}
	}

	clickRateComparator(a: any, b: any, c: any, d: any): number {
		if (parseFloat(c.click_rate) < parseFloat(d.click_rate)) {
			return 1;
		}
		if (parseFloat(c.click_rate) > parseFloat(d.click_rate)) {
			return -1;
		}
	}

	viewThroughComparator(a: any, b: any, c: any, d: any): number {
		if (parseFloat(c.view_throughs) < parseFloat(d.view_throughs)) {
			return 1;
		}
		if (parseFloat(c.view_throughs) > parseFloat(d.view_throughs)) {
			return -1;
		}
	}

	viewThroughRateComparator(a: any, b: any, c: any, d: any): number {
		if (parseFloat(c.viewthroughrate) < parseFloat(d.viewthroughrate)) {
			return 1;
		}
		if (parseFloat(c.viewthroughrate) > parseFloat(d.viewthroughrate)) {
			return -1;
		}
	}
	youtubeImpressionsComparator(a: any, b: any, c: any, d: any): number {
		if (parseFloat(c.youtube_verified_imprs) < parseFloat(d.youtube_verified_imprs)) {
			return 1;
		}
		if (parseFloat(c.youtube_verified_imprs) > parseFloat(d.youtube_verified_imprs)) {
			return -1;
		}
	}

	youtubeRateComparator(a: any, b: any, c: any, d: any): number {
		if (parseFloat(c.youtube_verified_imprs_rate) < parseFloat(d.youtube_verified_imprs_rate)) {
			return 1;
		}
		if (parseFloat(c.youtube_verified_imprs_rate) > parseFloat(d.youtube_verified_imprs_rate)) {
			return -1;
		}
	}

	drawDonutChart(): void {
		Highcharts.setOptions({
			lang: {
				thousandsSep: ','
			}
		});
		if (this.householdData.length > 0) {
			this.chartObj = Highcharts.chart({
				chart: {
					backgroundColor: 'transparent',
					margin: [50, 60, 40, 90],
					renderTo: this.chartElement.nativeElement,
					type: 'column'
				},
				credits: {
					enabled: false
				},
				legend: {
					enabled: true,
					align: 'center',
					verticalAlign: 'top',
					margin: 30,
					symbolRadius: 0
				},
				plotOptions: {
					column: {
						borderWidth: 0,
						pointPadding: 0.2
					},
					series: {
						states: {
							inactive: {
								opacity: 1
							}
						}
					}
				},
				series: [
					{
						data: this.seriesData,
						name: UtilityService.upperCaseFirst(this.groupby),
						type: 'column'
					},
					{
						data: this.householdData,
						name: 'Household Site Visits',
						type: 'column'
					}
				],
				subtitle: {
					text: null
				},
				title: {
					text: null
				},
				tooltip: {
					enabled: true
				},
				xAxis: {
					categories: this.xAxisChartTitles,
					labels: {
						style: {
							fontSize: '1.1em'
						}
					}
				},
				yAxis: {
					gridLineDashStyle: 'LongDash',
					gridLineWidth: 0,
					min: 0,
					title: {
						text: 'Count'
					}
				}
			});
		} else if (this.websiteAttributionsData.length > 0) {
			this.chartObj = Highcharts.chart({
				chart: {
					backgroundColor: 'transparent',
					margin: [50, 60, 40, 90],
					renderTo: this.chartElement.nativeElement,
					type: 'column'
				},
				credits: {
					enabled: false
				},
				legend: {
					enabled: true,
					align: 'center',
					verticalAlign: 'top',
					margin: 30,
					symbolRadius: 0
				},
				plotOptions: {
					column: {
						borderWidth: 0,
						pointPadding: 0.2
					},
					series: {
						states: {
							inactive: {
								opacity: 1
							}
						}
					}
				},
				series: [
					{
						data: this.seriesData,
						name: UtilityService.upperCaseFirst(this.groupby),
						type: 'column'
					},
					{
						data: this.websiteAttributionsData,
						name: 'Website Attributions',
						type: 'column'
					}
				],
				subtitle: {
					text: null
				},
				title: {
					text: null
				},
				tooltip: {
					enabled: true
				},
				xAxis: {
					categories: this.xAxisChartTitles,
					labels: {
						style: {
							fontSize: '1.1em'
						}
					}
				},
				yAxis: {
					gridLineDashStyle: 'LongDash',
					gridLineWidth: 0,
					min: 0,
					title: {
						text: 'Count'
					}
				}
			});
		} else {
			this.chartObj = Highcharts.chart({
				chart: {
					backgroundColor: 'transparent',
					margin: [50, 60, 40, 90],
					renderTo: this.chartElement.nativeElement,
					type: 'column'
				},
				credits: {
					enabled: false
				},
				legend: {
					enabled: true,
					align: 'center',
					verticalAlign: 'top',
					margin: 30,
					symbolRadius: 0
				},
				plotOptions: {
					column: {
						borderWidth: 0,
						pointPadding: 0.2
					},
					series: {
						states: {
							inactive: {
								opacity: 1
							}
						}
					}
				},
				series: [
					{
						data: this.seriesData,
						name: UtilityService.upperCaseFirst(this.groupby),
						type: 'column'
					}
				],
				subtitle: {
					text: null
				},
				title: {
					text: null
				},
				tooltip: {
					enabled: true
				},
				xAxis: {
					categories: this.xAxisChartTitles,
					labels: {
						style: {
							fontSize: '1.1em'
						}
					}
				},
				yAxis: {
					gridLineDashStyle: 'LongDash',
					gridLineWidth: 0,
					min: 0,
					title: {
						text: 'Count'
					}
				}
			});
		}
	}

	downloadCSV(e: any): void {
		e.preventDefault();
		this.analyticsService.downloadCSV(
			this.downloadConfiguration.csvQueryKey,
			this.downloadConfiguration.fileName,
			this.downloadCSVColumns
		);
	}

	protected addFooterToTableData(): void {
		const impressionsSum = this.calculateSumFromArray(_.pluck(this.tableData, 'impressions'));
		this.totalObj = { impressions: impressionsSum };
		if (this.tableData && this.tableData.length > 0 && Object.keys(this.tableData[0]).includes('clicks')) {
			const clicksSum = this.calculateSumFromArray(_.pluck(this.tableData, 'clicks'));
			const clickRateSum = (clicksSum / impressionsSum) * 100;
			this.totalObj['clicks'] = clicksSum;
			this.totalObj['clickRate'] = clickRateSum;
		}
		if (this.tableData && this.tableData.length > 0 && Object.keys(this.tableData[0]).includes('cost')) {
			this.totalObj['cost'] = this.totalCost;
		}
		if (this.tableData && this.tableData.length > 0 && Object.keys(this.tableData[0]).includes('household_visits')) {
			this.totalObj['household_visits'] = this.calculateSumFromArray(_.pluck(this.tableData, 'household_visits'));
		}
		if (this.tableData && this.tableData.length > 0 && Object.keys(this.tableData[0]).includes('website_attributions')) {
			this.totalObj['website_attributions'] = this.calculateSumFromArray(_.pluck(this.tableData, 'website_attributions'));
		}
		if (this.youtubeViewsPermission) {
			if (
				this.tableData &&
				this.tableData.length > 0 &&
				Object.keys(this.tableData[0]).includes('youtube_verified_imprs')
			) {
				this.totalObj['youtube_verified_imprs'] = this.calculateSumFromArray(
					_.pluck(this.tableData, 'youtube_verified_imprs')
				);
			}
			if (
				this.tableData &&
				this.tableData.length > 0 &&
				Object.keys(this.tableData[0]).includes('youtube_verified_imprs_rate')
			) {
				this.totalObj['youtube_verified_imprs_rate'] =
					this.totalObj['youtube_verified_imprs'] > 0 && this.youtubeImpressions > 0
						? (this.totalObj['youtube_verified_imprs'] / this.youtubeImpressions) * 100
						: 0;
			}
		}
	}

	protected calculateSumFromArray(array: number[]): number {
		return _.reduce(
			array,
			(memo, num) => {
				return memo + num;
			},
			0
		);
	}
}
